import Phaser from 'phaser';
import { TILE_SIZE, CANVAS_WIDTH, CANVAS_HEIGHT, ENEMY_STATS, EnemyType } from '@/config/GameConfig';
import { ALL_MAPS, setActiveMap, MapDefinition, isMapCompleted, getMapBestStars } from '@/map/MapData';
import { MAP_INFERNAL, MAP_STRESS_TEST } from '@/map/MapDefs';
import { generateMap } from '@/map/ProceduralMapGenerator';
import { difficultyForTier, paramsForTier } from '@/config/DifficultyScaling';
import { SoundManager } from '@/systems/SoundManager';
import { CustomMapStore } from '@/systems/CustomMapStore';
import { CampaignOverrideStore } from '@/systems/CampaignOverrideStore';
import { GOD } from '@/config/GodMode';
import { ResourceManager } from '@/systems/ResourceManager';
import { TECH, drawTechRect, fillTechRect, createTechButton, createSmallTechButton, drawMetalPlate, addShineEffect } from '@/ui/TechUI';
import { isDevMode } from '@/systems/DevConsole';
import { t } from '@/i18n/I18n';

const SCENE_W = CANVAS_WIDTH;
const SCENE_H = CANVAS_HEIGHT;

// Number of built-in official maps (indices 0..OFFICIAL_COUNT-1)
const OFFICIAL_COUNT = 11;

const DIFF_COLORS: Record<string, number> = {
  Beginner: 0x66dd66,
  Easy: 0x44cc44,
  Normal: 0xaacc44,
  Moderate: 0xccaa22,
  Challenging: 0xddaa00,
  Hard: 0xcc6622,
  'Very Hard': 0xcc4444,
  Expert: 0xaa22cc,
  Nightmare: 0xff2222,
  Infernal: 0x880000,
};

// Tabs for map list filtering
enum MapTab { OFFICIAL, CUSTOM }

/** Per-card rest state on the "table". */
interface CardRest {
  x: number;
  y: number;
  rotation: number;
  depth: number;
  globalIdx: number;
}

export class MapSelectScene extends Phaser.Scene {
  private selectedIdx = 0;
  private cards: Phaser.GameObjects.Container[] = [];
  private cardRests: CardRest[] = [];
  private hoveredCard: Phaser.GameObjects.Container | null = null;
  private previewContainer!: Phaser.GameObjects.Container;
  private infoText!: Phaser.GameObjects.Text;
  private startBtn!: Phaser.GameObjects.Container;
  private scrollContainer!: Phaser.GameObjects.Container;
  private scrollMask!: Phaser.GameObjects.Graphics;
  private cardScrollY = 0;
  private maxCardScroll = 0;

  // Current tab
  private activeTab: MapTab = MapTab.OFFICIAL;
  private tabOfficial!: Phaser.GameObjects.Text;
  private tabCustom!: Phaser.GameObjects.Text;

  // Custom map action buttons (shown only when a custom map is selected)
  private customActions!: Phaser.GameObjects.Container;
  // Dev-only edit button for official maps
  private devEditBtn!: Phaser.GameObjects.Container;
  // Create map button (shown only in custom tab)
  private createMapBtn!: Phaser.GameObjects.Container;
  // Import map button (shown only in custom tab)
  private importMapBtn!: Phaser.GameObjects.Container;

  // Infinite mode: generated map (regenerated on each selection or play)
  private infiniteMap: MapDefinition | null = null;

  // Card layout constants
  private static readonly CARD_W = 220;
  private static readonly CARD_H = 60;
  // Scatter zone (left panel)
  private static readonly TABLE_X = 30;
  private static readonly TABLE_Y = 100;
  private static readonly TABLE_W = 440;
  private static readonly TABLE_H = 600;
  // Focus position (where selected card flies to — vertically centered)
  private static readonly FOCUS_X = 250;
  private static readonly FOCUS_Y = CANVAS_HEIGHT / 2;

  constructor() {
    super({ key: 'MapSelectScene' });
  }

  create(data?: { tab?: 'CUSTOM' | 'OFFICIAL' }): void {
    this.cards = [];
    this.selectedIdx = 0;
    this.activeTab = data?.tab === 'CUSTOM' ? MapTab.CUSTOM : MapTab.OFFICIAL;

    this.cameras.main.setBackgroundColor('#0d0d1a');
    SoundManager.playRandomMapSelectMusic();

    // Title
    this.add.text(SCENE_W / 2, 30, t('mapSelect.title'), {
      fontSize: '28px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    // Back to title button
    const backBtn = createSmallTechButton(this, 16 + 40, 24, t('mapSelect.back'), 0xaa8888, () => {
      SoundManager.play('click');
      this.scene.start('TitleScene');
    });
    backBtn.setDepth(10);

    // Tabs
    this.buildTabs();

    // Card container + scroll mask over the scatter zone
    this.scrollContainer = this.add.container(0, 0);
    this.scrollMask = this.make.graphics({ x: 0, y: 0 });
    this.scrollMask.fillRect(
      MapSelectScene.TABLE_X - 10, MapSelectScene.TABLE_Y - 10,
      MapSelectScene.TABLE_W + 20, MapSelectScene.TABLE_H + 20,
    );
    this.scrollContainer.setMask(this.scrollMask.createGeometryMask());

    // Mouse wheel scrolling
    this.input.on('wheel', (_p: Phaser.Input.Pointer, _go: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
      if (this.maxCardScroll <= 0) return;
      this.cardScrollY = Phaser.Math.Clamp(
        this.cardScrollY + dy * 0.8,
        0, this.maxCardScroll,
      );
      this.scrollContainer.y = -this.cardScrollY;
    });

    // ESC → back to title
    this.input.keyboard?.on('keydown-ESC', () => {
      SoundManager.play('click');
      this.scene.start('TitleScene');
    });

    // Preview area (right panel)
    this.previewContainer = this.add.container(520, 100);

    // Info text (hidden — no longer shown)
    this.infoText = this.add.text(0, 0, '').setVisible(false);

    // Challenge rules (right side, below map list area)
    this.buildRulesPanel();

    // Start button
    this.buildStartButton();

    // Custom map action buttons (edit / delete / export)
    this.buildCustomActions();

    // Dev-only edit button for official maps (next to START button)
    this.devEditBtn = createSmallTechButton(this,
      SCENE_W / 2 + 80 - 140, SCENE_H - 45,
      'DEV EDIT', 0xff6644, () => {
        const map = ALL_MAPS[this.selectedIdx];
        if (!map) return;
        SoundManager.play('click');
        // Official maps: use CampaignOverrideStore (same as LevelTreeScene)
        if (this.selectedIdx < OFFICIAL_COUNT) {
          const override = CampaignOverrideStore.get(map.id);
          this.scene.start('MapEditorScene', {
            mapDef: override ?? map,
            campaignLevelId: map.id,
            hasOverride: !!override,
            returnTo: 'MapSelectScene',
          });
        } else {
          this.scene.start('MapEditorScene', { mapDef: map, returnTo: 'MapSelectScene' });
        }
      });
    this.devEditBtn.setDepth(50).setVisible(false);

    // Create map button (custom tab only) — left side, above action buttons
    this.createMapBtn = createSmallTechButton(this,
      SCENE_W / 2 - 200, SCENE_H - 95,
      t('mapSelect.createMap'), TECH.SUCCESS, () => {
        SoundManager.play('click');
        this.scene.start('MapEditorScene', { returnTo: 'MapSelectScene' });
      });
    this.createMapBtn.setDepth(10).setVisible(false);

    // Import map button (custom tab only) — below create button
    // Supports both single map JSON and array of maps (batch import)
    this.importMapBtn = createSmallTechButton(this,
      SCENE_W / 2 - 200, SCENE_H - 65,
      t('mapSelect.importMap'), TECH.ACCENT, () => {
        SoundManager.play('click');
        CustomMapStore.importBatchFromFile().then(count => {
          if (count > 0) this.rebuildCardList();
        });
      });
    this.importMapBtn.setDepth(10).setVisible(false);

    // Build card list for current tab and select first
    this.rebuildCardList();
  }

  // ── Tabs ──

  private buildTabs(): void {
    const tabY = 75;
    const tabW = 90;
    const tabH = 22;

    // Official tab
    const offGfx = this.add.graphics();
    const offText = this.add.text(MapSelectScene.TABLE_X + tabW / 2, tabY, t('mapSelect.official'), {
      fontSize: '12px', fontFamily: 'monospace', fontStyle: 'bold',
      color: '#ffcc00',
    }).setOrigin(0.5);
    const offHit = this.add.rectangle(MapSelectScene.TABLE_X + tabW / 2, tabY, tabW, tabH, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.tabOfficial = offText;

    // Custom tab
    const custGfx = this.add.graphics();
    const custText = this.add.text(MapSelectScene.TABLE_X + tabW + 10 + tabW / 2, tabY, t('mapSelect.custom'), {
      fontSize: '12px', fontFamily: 'monospace', fontStyle: 'bold',
      color: '#888888',
    }).setOrigin(0.5);
    const custHit = this.add.rectangle(MapSelectScene.TABLE_X + tabW + 10 + tabW / 2, tabY, tabW, tabH, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.tabCustom = custText;

    // Store graphics refs for redraw
    (this as any)._tabOffGfx = offGfx;
    (this as any)._tabCustGfx = custGfx;

    const drawTabs = () => {
      const isOfficial = this.activeTab === MapTab.OFFICIAL;
      offGfx.clear();
      drawTechRect(offGfx, MapSelectScene.TABLE_X, tabY - tabH / 2, tabW, tabH,
        isOfficial ? TECH.ACCENT : TECH.DIM, isOfficial ? 0.9 : 0.4, 1);
      offText.setColor(isOfficial ? '#ffcc00' : '#888888');

      custGfx.clear();
      drawTechRect(custGfx, MapSelectScene.TABLE_X + tabW + 10, tabY - tabH / 2, tabW, tabH,
        !isOfficial ? TECH.ACCENT : TECH.DIM, !isOfficial ? 0.9 : 0.4, 1);
      custText.setColor(!isOfficial ? '#ffcc00' : '#888888');
    };
    drawTabs();

    offHit.on('pointerdown', () => {
      if (this.activeTab === MapTab.OFFICIAL) return;
      SoundManager.play('click');
      this.activeTab = MapTab.OFFICIAL;
      drawTabs();
      this.rebuildCardList();
    });

    custHit.on('pointerdown', () => {
      if (this.activeTab === MapTab.CUSTOM) return;
      SoundManager.play('click');
      this.activeTab = MapTab.CUSTOM;
      drawTabs();
      this.rebuildCardList();
    });
  }

  private updateTabStyles(): void {
    const isOfficial = this.activeTab === MapTab.OFFICIAL;
    this.tabOfficial.setColor(isOfficial ? '#ffcc00' : '#888888');
    this.tabCustom.setColor(!isOfficial ? '#ffcc00' : '#888888');

    // Show custom count badge
    const customCount = ALL_MAPS.length - OFFICIAL_COUNT;
    this.tabCustom.setText(customCount > 0 ? `${t('mapSelect.custom')} (${customCount})` : t('mapSelect.custom'));
  }

  // ── Card list ──

  private getFilteredMaps(): { map: MapDefinition; globalIdx: number }[] {
    // Workaround: Vite HMR may serve a stale MapDefs.ts where ALL_MAPS has only 9 entries.
    // MAP_INFERNAL & MAP_STRESS_TEST are imported directly and appended if missing.
    // Safe: idempotent check prevents duplicates on repeated calls.
    if (!ALL_MAPS.find(m => m.id === 'infernal') && MAP_INFERNAL) {
      ALL_MAPS.push(MAP_INFERNAL);
    }
    if (!ALL_MAPS.find(m => m.id === 'stress_test') && MAP_STRESS_TEST) {
      ALL_MAPS.push(MAP_STRESS_TEST);
    }
    const result: { map: MapDefinition; globalIdx: number }[] = [];
    const devOn = isDevMode();
    for (let i = 0; i < ALL_MAPS.length; i++) {
      const m = ALL_MAPS[i];
      // Stress test only visible in dev mode
      if (m.id === 'stress_test' && !devOn) continue;
      const isOfficial = i < OFFICIAL_COUNT;
      if (this.activeTab === MapTab.OFFICIAL && isOfficial) {
        result.push({ map: m, globalIdx: i });
      } else if (this.activeTab === MapTab.CUSTOM && !isOfficial) {
        result.push({ map: m, globalIdx: i });
      }
    }
    return result;
  }

  private rebuildCardList(): void {
    // Clear old cards
    this.scrollContainer.removeAll(true);
    this.cards = [];
    this.cardRests = [];
    this.hoveredCard = null;

    // Show create/import buttons only in custom tab
    this.createMapBtn.setVisible(this.activeTab === MapTab.CUSTOM);
    this.importMapBtn.setVisible(this.activeTab === MapTab.CUSTOM);

    const filtered = this.getFilteredMaps();

    if (filtered.length === 0) {
      const emptyText = this.add.text(
        MapSelectScene.TABLE_X + MapSelectScene.TABLE_W / 2,
        MapSelectScene.TABLE_Y + 40,
        t('mapSelect.noCustomMaps') + '\nUse the Map Editor to create one,\nor import a friend\'s map!',
        { fontSize: '12px', color: '#666688', fontFamily: 'monospace', align: 'center' },
      ).setOrigin(0.5, 0);
      this.scrollContainer.add(emptyText);

      // Import + Create buttons on empty custom tab
      if (this.activeTab === MapTab.CUSTOM) {
        const btnCx = MapSelectScene.TABLE_X + MapSelectScene.TABLE_W / 2;
        const importBtn = createSmallTechButton(this, btnCx, MapSelectScene.TABLE_Y + 140,
          t('mapSelect.importMap'), TECH.COLOR, () => {
            SoundManager.play('click');
            CustomMapStore.importBatchFromFile().then(count => {
              if (count > 0) this.rebuildCardList();
            });
          });
        this.scrollContainer.add(importBtn);

        const createBtn = createSmallTechButton(this, btnCx, MapSelectScene.TABLE_Y + 180,
          t('mapSelect.createMap'), TECH.SUCCESS, () => {
            SoundManager.play('click');
            this.scene.start('MapEditorScene');
          });
        this.scrollContainer.add(createBtn);
      }

      this.previewContainer.removeAll(true);
      this.infoText.setText('');
      this.startBtn.setVisible(false);
      this.updateCustomActions();
      return;
    }

    this.startBtn.setVisible(true);

    // ── Scatter positions: rough grid with random offsets & rotations ──
    const cols = 2;
    const cellW = MapSelectScene.TABLE_W / cols;
    const cellH = MapSelectScene.CARD_H + 28;
    const isOfficialTab = this.activeTab === MapTab.OFFICIAL;
    const rng = (seed: number) => {
      // Simple seeded pseudo-random for consistent scatter
      const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
      return x - Math.floor(x);
    };

    for (let di = 0; di < filtered.length; di++) {
      const { map, globalIdx } = filtered[di];

      // ── Lock logic: official maps are locked until previous map is completed ──
      const isLocked = isOfficialTab && di > 0 && !isMapCompleted(filtered[di - 1].map.id);

      // Grid position + random scatter
      const col = di % cols;
      const row = Math.floor(di / cols);
      const cx = MapSelectScene.TABLE_X + col * cellW + cellW / 2;
      const cy = MapSelectScene.TABLE_Y + row * cellH + cellH / 2;
      const offX = (rng(di * 3) - 0.5) * 30;
      const offY = (rng(di * 7 + 1) - 0.5) * 16;
      const rot = (rng(di * 11 + 2) - 0.5) * 0.22; // ±~6°
      const restDepth = 1 + di;

      const restX = cx + offX;
      const restY = cy + offY;

      this.cardRests.push({ x: restX, y: restY, rotation: rot, depth: restDepth, globalIdx });

      // ── Build card container (origin = center of card) ──
      const container = this.add.container(restX, restY);
      container.setRotation(rot);
      container.setDepth(restDepth);
      container.setData('locked', isLocked);

      const hw = MapSelectScene.CARD_W / 2;
      const hh = MapSelectScene.CARD_H / 2;

      // Shadow (behind plate, only visible when card is on the table)
      const shadowGfx = this.add.graphics();
      shadowGfx.fillStyle(0x000000, 0.35);
      shadowGfx.fillRoundedRect(-hw + 3, -hh + 3, MapSelectScene.CARD_W, MapSelectScene.CARD_H, 3);
      container.add(shadowGfx);

      // Metallic plate (darker if locked)
      const plateGfx = this.add.graphics();
      drawMetalPlate(plateGfx, -hw, -hh, MapSelectScene.CARD_W, MapSelectScene.CARD_H,
        isLocked ? 0x333344 : 0x556677);
      container.add(plateGfx);

      // Synchronized shine sweep across all cards (skip locked)
      if (!isLocked) {
        const sweep = addShineEffect(this, container, MapSelectScene.CARD_W, MapSelectScene.CARD_H, 4000);
        container.setData('sweep', sweep);
      }

      // Hit area (centered)
      const bg = this.add.rectangle(0, 0, MapSelectScene.CARD_W, MapSelectScene.CARD_H, 0x000000, 0);
      container.add(bg);

      // Medal area reserve
      const completed = isMapCompleted(map.id);
      const textMaxW = completed ? MapSelectScene.CARD_W - 40 : MapSelectScene.CARD_W - 16;

      // Map name (dimmed if locked)
      const mapNum = isOfficialTab ? `${di + 1}. ` : '';
      const name = this.add.text(-hw + 11, -hh + 6,
        isLocked ? `${mapNum}???` : `${mapNum}${map.name}`, {
        fontSize: '12px', color: isLocked ? '#555566' : '#ffffff',
        fontFamily: 'monospace', fontStyle: 'bold',
        wordWrap: { width: textMaxW - 66 },
      });
      container.add(name);

      // Difficulty badge
      const diffColor = DIFF_COLORS[map.difficulty] ?? 0xaaaaaa;
      const badgeX = Math.min(hw - 38, textMaxW / 2 - 25);
      const badge = this.add.rectangle(badgeX, -hh + 14, 56, 13,
        isLocked ? 0x333344 : diffColor, isLocked ? 0.2 : 0.3)
        .setStrokeStyle(1, isLocked ? 0x444455 : diffColor);
      container.add(badge);
      const diffText = this.add.text(badgeX, -hh + 14, map.difficulty, {
        fontSize: '8px', color: isLocked ? '#444455' : '#ffffff', fontFamily: 'monospace',
      }).setOrigin(0.5);
      container.add(diffText);

      if (isLocked) {
        // Lock icon overlay
        const lockIcon = this.add.text(0, -2, '🔒', {
          fontSize: '18px',
        }).setOrigin(0.5).setAlpha(0.6);
        container.add(lockIcon);

        const lockHint = this.add.text(0, hh - 10, 'Complete previous level to unlock', {
          fontSize: '7px', color: '#555566', fontFamily: 'monospace',
        }).setOrigin(0.5);
        container.add(lockHint);
      } else {
        // Info line
        const pathCount = map.paths.length;
        const infoLine = this.add.text(-hw + 11, -hh + 26,
          `${map.cols}×${map.rows}  |  ${pathCount} path${pathCount > 1 ? 's' : ''}`,
          { fontSize: '9px', color: '#ccccdd', fontFamily: 'monospace' },
        );
        container.add(infoLine);

        // Description
        const desc = this.add.text(-hw + 11, -hh + 40, map.description, {
          fontSize: '8px', color: '#99aacc', fontFamily: 'monospace',
          wordWrap: { width: textMaxW - 14 },
        });
        container.add(desc);

        // Best medal display
        if (completed) {
          const bestStars = getMapBestStars(map.id);
          const medalTier = bestStars >= 3 ? 'GOLD' : bestStars >= 2 ? 'SILVER' : bestStars >= 1 ? 'BRONZE' : 'IRON';
          const medalColor = bestStars >= 3 ? '#ffcc00' : bestStars >= 2 ? '#cccccc' : bestStars >= 1 ? '#cc8844' : '#777788';
          const borderHex = bestStars >= 3 ? 0xffcc00 : bestStars >= 2 ? 0xcccccc : bestStars >= 1 ? 0xcc8844 : 0x44ff44;

          plateGfx.lineStyle(2, borderHex, 0.7);
          plateGfx.strokeRect(-hw, -hh, MapSelectScene.CARD_W, MapSelectScene.CARD_H);

          // Stars row
          const starY = -4;
          for (let s = 0; s < 3; s++) {
            const filled = s < bestStars;
            const star = this.add.text(hw - 28 + s * 12, starY, '★', {
              fontSize: '11px', color: filled ? '#ffcc00' : '#333355', fontFamily: 'monospace', fontStyle: 'bold',
            }).setOrigin(0.5);
            container.add(star);
          }

          // Medal tier label
          const tierLabel = this.add.text(hw - 16, starY + 12, medalTier, {
            fontSize: '7px', color: medalColor, fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5);
          container.add(tierLabel);
        }
      }

      // ── Interactions: click to select (lift/drop handled in selectMap) ──
      const capturedIdx = globalIdx;
      if (!isLocked) {
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', () => {
          SoundManager.play('clickSelect');
          this.selectMap(capturedIdx);
        });
      }

      this.scrollContainer.add(container);
      this.cards.push(container);
    }

    // ── Infinite mode card (at end of official maps) ──
    let totalCards = filtered.length;
    if (isOfficialTab) {
      const infDi = filtered.length;
      const infCol = infDi % cols;
      const infRow = Math.floor(infDi / cols);
      const infCx = MapSelectScene.TABLE_X + infCol * cellW + cellW / 2;
      const infCy = MapSelectScene.TABLE_Y + infRow * cellH + cellH / 2;
      const infRestX = infCx;
      const infRestY = infCy;
      const INFINITE_IDX = -1;

      // Locked until last official map is completed
      const lastOfficialMap = filtered[filtered.length - 1]?.map;
      const infiniteLocked = !lastOfficialMap || !isMapCompleted(lastOfficialMap.id);

      this.cardRests.push({ x: infRestX, y: infRestY, rotation: 0, depth: 1 + infDi, globalIdx: INFINITE_IDX });

      const infContainer = this.add.container(infRestX, infRestY);
      infContainer.setDepth(1 + infDi);
      infContainer.setData('locked', infiniteLocked);
      infContainer.setData('infinite', true);

      const hw = MapSelectScene.CARD_W / 2;
      const hh = MapSelectScene.CARD_H / 2;

      // Shadow
      const infShadow = this.add.graphics();
      infShadow.fillStyle(0x000000, 0.35);
      infShadow.fillRoundedRect(-hw + 3, -hh + 3, MapSelectScene.CARD_W, MapSelectScene.CARD_H, 3);
      infContainer.add(infShadow);

      // Plate — special purple/red gradient look
      const infPlate = this.add.graphics();
      drawMetalPlate(infPlate, -hw, -hh, MapSelectScene.CARD_W, MapSelectScene.CARD_H,
        infiniteLocked ? 0x333344 : 0x662244);
      if (!infiniteLocked) {
        infPlate.lineStyle(2, 0xaa22cc, 0.8);
        infPlate.strokeRect(-hw, -hh, MapSelectScene.CARD_W, MapSelectScene.CARD_H);
      }
      infContainer.add(infPlate);

      if (!infiniteLocked) {
        const sweep = addShineEffect(this, infContainer, MapSelectScene.CARD_W, MapSelectScene.CARD_H, 3000);
        infContainer.setData('sweep', sweep);
      }

      // Hit area
      const infBg = this.add.rectangle(0, 0, MapSelectScene.CARD_W, MapSelectScene.CARD_H, 0x000000, 0);
      infContainer.add(infBg);

      if (infiniteLocked) {
        const lockName = this.add.text(-hw + 11, -hh + 6, `∞ INFINITE`, {
          fontSize: '12px', color: '#555566', fontFamily: 'monospace', fontStyle: 'bold',
        });
        infContainer.add(lockName);
        const lockIcon = this.add.text(0, -2, '🔒', { fontSize: '18px' }).setOrigin(0.5).setAlpha(0.6);
        infContainer.add(lockIcon);
        const lockHint = this.add.text(0, hh - 10, 'Complete all levels to unlock', {
          fontSize: '7px', color: '#555566', fontFamily: 'monospace',
        }).setOrigin(0.5);
        infContainer.add(lockHint);
      } else {
        const infName = this.add.text(-hw + 11, -hh + 6, '∞ INFINITE', {
          fontSize: '12px', color: '#ff66ff', fontFamily: 'monospace', fontStyle: 'bold',
        });
        infContainer.add(infName);

        // Difficulty badge
        const infBadge = this.add.rectangle(hw - 38, -hh + 14, 56, 13, 0xaa22cc, 0.3)
          .setStrokeStyle(1, 0xaa22cc);
        infContainer.add(infBadge);
        const infDiffText = this.add.text(hw - 38, -hh + 14, 'Random', {
          fontSize: '8px', color: '#ffffff', fontFamily: 'monospace',
        }).setOrigin(0.5);
        infContainer.add(infDiffText);

        const infDesc = this.add.text(-hw + 11, -hh + 26,
          'Random map every time\nExpert to Infernal difficulty', {
          fontSize: '9px', color: '#cc88ee', fontFamily: 'monospace',
          wordWrap: { width: MapSelectScene.CARD_W - 30 },
        });
        infContainer.add(infDesc);

        infBg.setInteractive({ useHandCursor: true });
        infBg.on('pointerdown', () => {
          SoundManager.play('clickSelect');
          this.selectMap(INFINITE_IDX);
        });
      }

      this.scrollContainer.add(infContainer);
      this.cards.push(infContainer);
      totalCards++;
    }

    // Calculate scroll bounds
    const totalRows = Math.ceil(totalCards / cols);
    const totalContentH = totalRows * cellH + 40;
    this.maxCardScroll = Math.max(0, totalContentH - MapSelectScene.TABLE_H);
    this.cardScrollY = 0;
    this.scrollContainer.y = 0;

    // Select first map in this tab
    this.selectMap(filtered[0].globalIdx);
  }

  /** Lift a card out of the pile: fly to focus position, straighten, scale up. */
  private liftCard(card: Phaser.GameObjects.Container, _di: number): void {
    this.hoveredCard = card;
    // Move to top of container draw order (containers ignore depth, use child order)
    this.scrollContainer.bringToTop(card);
    // Focus Y accounts for scroll so card is always visible on screen
    const focusY = MapSelectScene.FOCUS_Y + this.cardScrollY;
    this.tweens.killTweensOf(card);
    this.tweens.add({
      targets: card,
      x: MapSelectScene.FOCUS_X,
      y: focusY,
      scaleX: 1.25,
      scaleY: 1.25,
      rotation: 0,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Subtle idle bob on selected card
        this.tweens.add({
          targets: card,
          y: focusY - 3,
          duration: 1200,
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        });
        // Trigger shine sweep on selection
        const sweep = card.getData('sweep') as (() => void) | undefined;
        if (sweep) sweep();
      },
    });
  }

  /** Drop a card back to its rest position on the table. */
  private dropCard(card: Phaser.GameObjects.Container, di: number): void {
    if (this.hoveredCard === card) this.hoveredCard = null;
    const rest = this.cardRests[di];
    if (!rest) return;
    this.tweens.killTweensOf(card);
    this.tweens.add({
      targets: card,
      x: rest.x,
      y: rest.y,
      scaleX: 1,
      scaleY: 1,
      rotation: rest.rotation,
      duration: 300,
      ease: 'Sine.easeOut',
    });
  }

  private selectMap(globalIdx: number): void {
    const filtered = this.getFilteredMaps();
    const hw = MapSelectScene.CARD_W / 2;
    const hh = MapSelectScene.CARD_H / 2;

    // Find display index — for infinite card (globalIdx=-1), it's appended after filtered
    const findDi = (idx: number) => {
      if (idx === -1) return this.cards.length - 1; // infinite card is last
      return filtered.findIndex(f => f.globalIdx === idx);
    };

    const newDi = findDi(globalIdx);
    const prevDi = findDi(this.selectedIdx);

    // Drop previously lifted card back to table (if different)
    if (prevDi >= 0 && prevDi !== newDi && this.cards[prevDi]) {
      this.dropCard(this.cards[prevDi], prevDi);
    }

    // Lift newly selected card
    if (newDi >= 0 && this.cards[newDi]) {
      this.liftCard(this.cards[newDi], newDi);
    }

    // Update card borders
    this.cards.forEach((card, displayIdx) => {
      const locked = card.getData('locked') as boolean;
      const isInfinite = card.getData('infinite') as boolean;
      const plateGfx = card.getAt(1) as Phaser.GameObjects.Graphics;
      const cardGlobalIdx = this.cardRests[displayIdx]?.globalIdx;
      const isSelected = cardGlobalIdx === globalIdx;
      const completed = filtered[displayIdx] ? isMapCompleted(filtered[displayIdx].map.id) : false;
      plateGfx.clear();
      const plateColor = locked ? 0x333344 : isInfinite ? 0x662244 : 0x556677;
      drawMetalPlate(plateGfx, -hw, -hh, MapSelectScene.CARD_W, MapSelectScene.CARD_H, plateColor);
      const borderColor = locked ? 0x222233
        : isSelected ? 0xffcc00
        : isInfinite ? 0xaa22cc
        : (completed ? 0x44ff44 : 0x333366);
      plateGfx.lineStyle(2, borderColor, locked ? 0.4 : 0.8);
      plateGfx.strokeRect(-hw, -hh, MapSelectScene.CARD_W, MapSelectScene.CARD_H);
    });

    this.selectedIdx = globalIdx;

    // Handle infinite mode selection
    if (globalIdx === -1) {
      this.updateCustomActions();
      // Generate a fresh random map for preview
      this.infiniteMap = this.generateInfiniteMap();
      this.renderPreview(this.infiniteMap);
      this.infoText.setText('');
      return;
    }

    const map = ALL_MAPS[globalIdx];
    if (!map) return;

    // Show/hide custom map action buttons
    this.updateCustomActions();

    // Update preview (custom maps always visible, official maps only after completion)
    const isCustom = globalIdx >= OFFICIAL_COUNT;
    if (isCustom || isMapCompleted(map.id)) {
      this.renderPreview(map);
    } else {
      this.previewContainer.removeAll(true);
      const lockText = this.add.text(275, 200, t('mapSelect.completeToReveal'), {
        fontSize: '14px', color: '#556677', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5);
      this.previewContainer.add(lockText);
    }

    // Update info text
    const spawnCount = new Set(map.paths.map(p => `${p[0].col},${p[0].row}`)).size;
    const baseCount = new Set(map.paths.map(p => {
      const last = p[p.length - 1];
      return `${last.col},${last.row}`;
    })).size;

    const enemyNames = map.allowedEnemies.map(e => ENEMY_STATS[e].label).join(', ');

    this.infoText.setText([
      `Map: ${map.name}  (${map.difficulty})`,
      `Size: ${map.cols}×${map.rows}  |  Spawns: ${spawnCount}  |  Bases: ${baseCount}`,
      `Waves: ${map.waveCount ?? '?'}  |  Paths: ${map.paths.length}`,
      ``,
      `Enemies: ${enemyNames}`,
      ``,
      map.description,
    ].join('\n'));
  }

  private renderPreview(map: MapDefinition): void {
    this.previewContainer.removeAll(true);

    const maxW = 550;
    const maxH = 440;
    const mapPixelW = map.cols * TILE_SIZE;
    const mapPixelH = map.rows * TILE_SIZE;
    const scale = Math.min(maxW / mapPixelW, maxH / mapPixelH, 1);
    const tileS = TILE_SIZE * scale;

    const COLORS: Record<number, number> = {
      0: 0x2a2a2a, 1: 0x444444, 2: 0x886633, 3: 0x335533, 4: 0x2244cc, 5: 0xcc2222,
    };

    for (let row = 0; row < map.rows; row++) {
      for (let col = 0; col < map.cols; col++) {
        const tile = map.grid[row][col];
        const color = COLORS[tile] ?? 0x1a1a1a;
        const x = col * tileS + tileS / 2;
        const y = row * tileS + tileS / 2;
        const rect = this.add.rectangle(x, y, tileS - 1, tileS - 1, color);
        this.previewContainer.add(rect);

        if (tile === 5) {
          const t = this.add.text(x, y, 'S', {
            fontSize: `${Math.max(8, tileS * 0.5)}px`, color: '#ffffff',
            fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5);
          this.previewContainer.add(t);
        }
        if (tile === 4) {
          const t = this.add.text(x, y, 'B', {
            fontSize: `${Math.max(8, tileS * 0.5)}px`, color: '#ffffff',
            fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5);
          this.previewContainer.add(t);
        }
      }
    }

    for (const path of map.paths) {
      for (let i = 0; i < path.length - 1; i++) {
        const from = path[i];
        const to = path[i + 1];
        const line = this.add.line(
          0, 0,
          from.col * tileS + tileS / 2, from.row * tileS + tileS / 2,
          to.col * tileS + tileS / 2, to.row * tileS + tileS / 2,
          0xffaa00, 0.6,
        );
        line.setLineWidth(2);
        line.setOrigin(0, 0);
        this.previewContainer.add(line);
      }
    }
  }

  private buildRulesPanel(): void {
    const rw = 280;
    const rx = SCENE_W - rw - 20;
    const ry = SCENE_H - 140;

    this.add.text(rx, ry, t('mapSelect.challengeRules'), {
      fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });

    const rules = [
      'Components unlocked in Campaign',
      'are available here.',
      '',
      'Grid size matches Campaign progress.',
      '',
      'Victory earns $ + partial resources.',
      '',
      'Beat a level to unlock the next one.',
      '',
      'Complete all levels to unlock Infinite!',
    ];

    this.add.text(rx, ry + 18, rules.join('\n'), {
      fontSize: '9px', color: '#777799', fontFamily: 'monospace',
      lineSpacing: 2, wordWrap: { width: rw },
    });
  }

  private buildStartButton(): void {
    const btnX = SCENE_W / 2;
    const btnY = SCENE_H - 45;
    this.startBtn = createTechButton(this, btnX, btnY, t('mapSelect.start'), {
      onClick: () => { SoundManager.play('clickConfirm'); this.onStart(); },
      color: TECH.SUCCESS,
      width: 220, height: 44, fontSize: '18px',
    });
  }

  private buildCustomActions(): void {
    const cx = SCENE_W / 2 + 80;
    const cy = SCENE_H - 95;
    this.customActions = this.add.container(cx, cy).setVisible(false).setDepth(10);

    const gap = 110;
    // Edit button
    const editBtn = createSmallTechButton(this, -gap, 0, t('mapSelect.edit'), TECH.COLOR, () => {
      const map = ALL_MAPS[this.selectedIdx];
      if (!map) return;
      SoundManager.play('click');
      this.scene.start('MapEditorScene', { mapId: map.id, returnTo: 'MapSelectScene' });
    });
    this.customActions.add(editBtn);

    // Export button
    const exportBtn = createSmallTechButton(this, 0, 0, t('mapSelect.export'), TECH.ACCENT, () => {
      const map = ALL_MAPS[this.selectedIdx];
      if (!map) return;
      SoundManager.play('click');
      CustomMapStore.exportToFile(map);
    });
    this.customActions.add(exportBtn);

    // Delete button
    const deleteBtn = createSmallTechButton(this, gap, 0, t('mapSelect.delete'), TECH.DANGER, () => {
      const map = ALL_MAPS[this.selectedIdx];
      if (!map) return;
      SoundManager.play('click');
      CustomMapStore.delete(map.id);
      this.rebuildCardList();
    });
    this.customActions.add(deleteBtn);
  }

  /** Show/hide custom action buttons based on selected map. */
  private updateCustomActions(): void {
    const isCustom = this.selectedIdx >= OFFICIAL_COUNT;
    this.customActions.setVisible(isCustom);
    // Dev-only: show edit button for official maps
    const isOfficial = this.selectedIdx >= 0 && this.selectedIdx < OFFICIAL_COUNT;
    this.devEditBtn.setVisible(isOfficial && isDevMode());
  }

  /** Generate a random infinite-mode map (Expert to Infernal difficulty) */
  private generateInfiniteMap(): MapDefinition {
    const seed = Date.now();
    // Random tier between Expert (24) and Infernal (35)
    const tier = 24 + Math.floor(Math.random() * 12);
    const difficulty = difficultyForTier(tier);
    const params = paramsForTier(tier, seed % 100);
    const allEnemies = Object.values(EnemyType).filter(v => typeof v === 'string') as EnemyType[];
    return generateMap(`infinite_${seed}`, `Infinite #${seed % 10000}`, seed, params, difficulty, allEnemies);
  }

  private onStart(): void {
    let map: MapDefinition;

    // Infinite mode: generate a new random map
    if (this.selectedIdx === -1) {
      map = this.generateInfiniteMap();
    } else {
      const m = ALL_MAPS[this.selectedIdx];
      if (!m) return;
      // Check for dev-edited override in CustomMapStore (official maps edited via DEV EDIT)
      const override = CustomMapStore.getById(m.id);
      map = override ?? m;
    }

    setActiveMap(map);

    // Challenge mode: clear campaign node so GameScene uses map-level params
    this.registry.set('currentLevelNode', undefined);
    // Challenge mode: load accumulated challenge treasury (separate economy)
    const treasury = ResourceManager.getChallengeTreasury();
    this.registry.set('startingResources', GOD.challengeStartingMoney + treasury.currency);
    this.registry.set('qpBaseUpgradeLevels', treasury.baseUpgradeLevels ?? {});

    if (this.scene.isActive('UIScene')) {
      this.scene.stop('UIScene');
    }

    this.scene.start('GameScene');
    this.scene.launch('UIScene');
    this.scene.stop('MapSelectScene');
  }
}
